import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAnchoredPortalMenu } from '../hooks/useAnchoredPortalMenu';
import './SearchPicker.css';

interface Props<T> {
  options: T[];
  value: string | number;
  getValue: (option: T) => string | number;
  getLabel: (option: T) => string;
  onChange: (option: T | null) => void;
  onSearch: (search: string) => void;
  placeholder: string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  disabled?: boolean;
  required?: boolean;
  size?: 'sm' | 'lg';
  'aria-label'?: string;
}

export default function SearchPicker<T>({ options, value, getValue, getLabel, onChange, onSearch,
  placeholder, loading, error, onRetry, disabled, required, size, 'aria-label': ariaLabel }: Props<T>) {
  const { t } = useTranslation('common');
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const selected = useRef<T | null>(null);
  const match = options.find(option => String(getValue(option)) === String(value));
  if (match) selected.current = match;
  if (!value || (selected.current && String(getValue(selected.current)) !== String(value))) selected.current = null;
  const label = selected.current ? getLabel(selected.current) : (value ? String(value) : '');
  const { controlRef, menuRef, menuPosition } = useAnchoredPortalMenu({ isOpen: open, preferredMaxHeight: 240, minWidth: 0 });
  const menuVisible = open && !!menuPosition;
  useEffect(() => { if (menuVisible) searchRef.current?.focus(); }, [menuVisible]);
  useEffect(() => { setText(null); }, [value]);
  useEffect(() => {
    inputRef.current?.setCustomValidity(required && !value ? placeholder : '');
  }, [required, value, placeholder]);
  const choose = (option: T | null) => {
    selected.current = option;
    onChange(option);
    setText(null);
    inputRef.current?.focus();
    setOpen(false);
  };
  return <div ref={controlRef} className="position-relative">
    <Form.Control className="form-select" ref={inputRef} type="text" role="combobox" aria-label={ariaLabel || placeholder}
      aria-expanded={open} aria-controls={id} aria-autocomplete="list" autoComplete="off"
      value={text ?? label} placeholder={placeholder} disabled={disabled} required={required} size={size}
      aria-busy={loading} onFocus={() => setOpen(true)} onClick={() => setOpen(true)}
      onBlur={event => {
        if (event.relatedTarget instanceof Node && menuRef.current?.contains(event.relatedTarget)) return;
        setOpen(false); setText(null);
      }}
      onChange={event => { setText(event.target.value); setSearchText(event.target.value); onSearch(event.target.value); setOpen(true); if (!event.target.value) onChange(null); }}
      onKeyDown={event => {
        if (event.key === 'Escape') { setOpen(false); setText(null); }
        if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus(); }
        if (event.key === 'Enter' && open) { event.preventDefault(); if (options.length === 1) choose(options[0]); }
      }} />
    {open && menuPosition && createPortal(<div ref={menuRef} className="an-floating-menu an-search-picker-menu"
      style={{ top: menuPosition.top, left: menuPosition.left, width: menuPosition.width, maxHeight: menuPosition.maxHeight, overflowY: 'auto' }}
      onMouseDown={event => { if ((event.target as HTMLElement).closest('button')) event.preventDefault(); }}
      onBlur={event => {
        if (event.relatedTarget instanceof Node && (menuRef.current?.contains(event.relatedTarget) || controlRef.current?.contains(event.relatedTarget))) return;
        setOpen(false); setText(null);
      }}
      onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); inputRef.current?.focus(); setOpen(false); }
        if (event.key === 'Enter' && event.target === searchRef.current) {
          event.preventDefault();
          if (!loading && options.length === 1) choose(options[0]);
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const buttons = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('button[role="option"]') || [])];
          const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
          const next = index < 0 ? (event.key === 'ArrowDown' ? 0 : buttons.length - 1)
            : (index + (event.key === 'ArrowDown' ? 1 : buttons.length - 1)) % buttons.length;
          buttons[next]?.focus();
        }
      }}>
      <div className="an-search-picker-header">
        <Form.Control ref={searchRef} type="search" size="sm" aria-label={t('buttons.search')}
          placeholder={t('buttons.search')} autoComplete="off" value={searchText}
          aria-controls={id} aria-busy={loading}
          onChange={event => { setSearchText(event.target.value); onSearch(event.target.value); }} />
      </div>
      <div id={id} role="listbox" aria-label={ariaLabel || placeholder}>
      <button type="button" role="option" aria-selected={!value} className="dropdown-item" onClick={() => choose(null)}>{placeholder}</button>
      {loading ? <div className="px-3 py-2"><Spinner size="sm" /> {t('loading.default')}</div> : options.map(option =>
        <button type="button" role="option" aria-selected={String(getValue(option)) === String(value)} key={getValue(option)}
          className="dropdown-item" onClick={() => choose(option)}>{getLabel(option)}</button>)}
      {!!error && <div className="px-3 py-2" role="alert">{String((error as { message?: string }).message || error)}
        <Button size="sm" variant="link" onClick={onRetry}>↻</Button></div>}
      </div>
    </div>, controlRef.current?.closest('.modal') ?? document.body)}
  </div>;
}
